import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { lettersAPI, paymentsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
    Shield, 
    ArrowLeft,
    FileText,
    Lock,
    CreditCard,
    Download,
    Copy,
    CheckCircle2,
    Printer,
    Edit3,
    FileDown
} from "lucide-react";
import { toast } from "sonner";
import PricingModal from "../components/PricingModal";

const LettersPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [previews, setPreviews] = useState([]);
    const [fullLetters, setFullLetters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [selectedLetter, setSelectedLetter] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [pricing, setPricing] = useState({ price: null, formatted: '…', monthly_formatted: '…' });
    const [pricingOpen, setPricingOpen] = useState(false);

    const loadPricing = useCallback(async () => {
        try {
            const response = await paymentsAPI.getPricing();
            setPricing(response.data);
        } catch (error) {
            // Pricing fetch is non-critical — defaults already set in state
        }
    }, []);

    const loadLetters = useCallback(async () => {
        setLoading(true);
        try {
            const previewResponse = await lettersAPI.getPreview();
            setPreviews(previewResponse.data);

            if (user?.has_paid) {
                try {
                    const fullResponse = await lettersAPI.getFull();
                    setFullLetters(fullResponse.data);
                    if (fullResponse.data.length > 0) {
                        setSelectedLetter(fullResponse.data[0]);
                    }
                } catch (error) {
                    toast.error(error.response?.data?.detail || "Could not load your full letters");
                }
            } else if (previewResponse.data.length > 0) {
                setSelectedLetter(previewResponse.data[0]);
            }
        } catch (error) {
            toast.error("No letters found. Please run an analysis first.");
            navigate("/dashboard");
        } finally {
            setLoading(false);
        }
    }, [navigate, user?.has_paid]);

    useEffect(() => {
        loadLetters();
        loadPricing();
    }, [loadLetters, loadPricing]);

    const handleUpgrade = () => {
        setPricingOpen(true);
    };

    const handleCopy = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        toast.success("Letter copied to clipboard");
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleDownloadPdf = async (letter) => {
        try {
            const response = await lettersAPI.downloadPdf(letter.id);
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(letter.letter_type || "dispute-letter").replace(/\s+/g, "_").toLowerCase()}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("PDF downloaded");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not download PDF");
        }
    };

    const handlePrint = (letter) => {
        // Use a sandboxed iframe with srcdoc (HTML-escaped) instead of document.write —
        // avoids XSS risk and is the current recommended approach for programmatic print.
        const escape = (s) => String(s ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

        const title = escape(letter.letter_type || "Dispute Letter");
        const body = escape(letter.full_text || "");
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title} - CreditFix AI</title><style>
            body { font-family: 'Times New Roman', serif; padding: 40px; line-height: 1.6; max-width: 800px; margin: 0 auto; }
            pre { white-space: pre-wrap; font-family: inherit; }
        </style></head><body><pre>${body}</pre><script>window.addEventListener('load',()=>{window.print();});</script></body></html>`;

        const iframe = document.createElement("iframe");
        iframe.setAttribute("sandbox", "allow-same-origin allow-modals allow-scripts");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        iframe.srcdoc = html;
        iframe.onload = () => {
            try {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
            } finally {
                setTimeout(() => iframe.remove(), 1000);
            }
        };
        document.body.appendChild(iframe);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
        );
    }

    const letters = user?.has_paid ? fullLetters : previews;

    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="nav-glass sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Shield className="h-8 w-8 text-primary" />
                        <span className="font-serif text-xl font-semibold">CreditFix AI</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        {user?.has_paid ? (
                            <Badge className="badge-premium">Full Access</Badge>
                        ) : (
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={handleUpgrade}
                                disabled={checkoutLoading}
                                className="rounded-full"
                                data-testid="upgrade-btn"
                            >
                                <Lock className="h-4 w-4 mr-2" />
                                Upgrade {pricing.formatted}
                            </Button>
                        )}
                    </div>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                {/* Back Button */}
                <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Dashboard
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <h1 className="text-3xl font-serif font-bold text-foreground mb-2">
                        Dispute Letters
                    </h1>
                    <p className="text-muted-foreground">
                        {user?.has_paid 
                            ? "Your professionally crafted dispute letters are ready to send."
                            : "Preview your letters. Upgrade to access the full text."}
                    </p>
                </motion.div>

                {/* Personalization Notice - Only for paid users */}
                {user?.has_paid && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="mb-6"
                    >
                        <Card className="border-accent/30 bg-accent/5">
                            <CardContent className="py-4 px-6">
                                <div className="flex items-start gap-3">
                                    <Edit3 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium text-foreground text-sm">Personalize Before Sending</p>
                                        <p className="text-sm text-muted-foreground">
                                            These letters are written in your voice (first person). Before sending, review each letter and add your personal details—specific dates, account numbers, and any additional context about your situation. The more genuine and specific your letter sounds, the more effective it will be.
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Letter List */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="lg:col-span-1"
                    >
                        <Card className="border-border">
                            <CardHeader>
                                <CardTitle className="text-lg font-serif">Your Letters</CardTitle>
                                <CardDescription>
                                    {letters.length} letters generated
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="p-0">
                                <ScrollArea className="h-[500px]">
                                    <div className="p-4 space-y-2">
                                        {letters.map((letter, index) => (
                                            <div
                                                key={letter.id}
                                                className={`p-4 rounded-lg cursor-pointer transition-colors ${
                                                    selectedLetter?.id === letter.id 
                                                        ? 'bg-primary/10 border border-primary/30' 
                                                        : 'hover:bg-muted border border-transparent'
                                                }`}
                                                onClick={() => setSelectedLetter(letter)}
                                                data-testid={`letter-item-${index}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <FileText className="h-5 w-5 text-primary mt-0.5" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-foreground truncate">
                                                            {letter.letter_type}
                                                        </p>
                                                        <p className="text-sm text-muted-foreground truncate">
                                                            To: {letter.recipient}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Letter Preview/Content */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="lg:col-span-2"
                    >
                        {selectedLetter ? (
                            <Card className="border-border">
                                <CardHeader>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <CardTitle className="font-serif">{selectedLetter.letter_type}</CardTitle>
                                            <CardDescription>To: {selectedLetter.recipient}</CardDescription>
                                        </div>
                                        {user?.has_paid && (
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleCopy(selectedLetter.full_text, selectedLetter.id)}
                                                    data-testid="copy-letter-btn"
                                                >
                                                    {copiedId === selectedLetter.id ? (
                                                        <CheckCircle2 className="h-4 w-4 text-accent" />
                                                    ) : (
                                                        <Copy className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handlePrint(selectedLetter)}
                                                    data-testid="print-letter-btn"
                                                >
                                                    <Printer className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleDownloadPdf(selectedLetter)}
                                                    data-testid="download-pdf-btn"
                                                >
                                                    <FileDown className="h-4 w-4 mr-1" />
                                                    PDF
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="mb-4 flex flex-wrap gap-2">
                                        {selectedLetter.fcra_codes?.map((code, i) => (
                                            <Badge key={i} variant="outline" className="text-xs">
                                                {code}
                                            </Badge>
                                        ))}
                                    </div>

                                    {user?.has_paid ? (
                                        <div className="space-y-3">
                                            <div className="relative">
                                                <ScrollArea className="h-[400px] rounded-lg border border-border bg-muted/30 p-6">
                                                    <pre className="text-sm font-mono whitespace-pre-wrap text-foreground" data-testid="letter-body">
                                                        {selectedLetter.full_text}
                                                    </pre>
                                                </ScrollArea>
                                            </div>
                                            {selectedLetter.personalization_tip && (
                                                <div
                                                    className="flex gap-3 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-4"
                                                    data-testid="personalization-tip"
                                                >
                                                    <Edit3 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                                    <div className="text-xs">
                                                        <p className="font-semibold text-primary uppercase tracking-wide mb-1">
                                                            Personalization tip (not part of the letter)
                                                        </p>
                                                        <p className="text-muted-foreground leading-relaxed">
                                                            {selectedLetter.personalization_tip}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <div className="h-[400px] rounded-lg border border-border bg-muted/30 p-6 overflow-hidden">
                                                <div className="letter-fade">
                                                    <pre className="text-sm font-mono whitespace-pre-wrap text-foreground">
                                                        {selectedLetter.preview_text}
                                                    </pre>
                                                </div>
                                            </div>
                                            
                                            {/* Locked Overlay */}
                                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-background via-background/80 to-transparent">
                                                <div className="text-center p-8">
                                                    <Lock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                                    <h3 className="font-serif text-lg font-semibold text-foreground mb-2">
                                                        Full Letter Locked
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                                                        Upgrade to access the complete letter with all legal citations and proper formatting.
                                                    </p>
                                                    <Button 
                                                        className="rounded-full btn-press"
                                                        onClick={handleUpgrade}
                                                        disabled={checkoutLoading}
                                                        data-testid="unlock-letter-btn"
                                                    >
                                                        {checkoutLoading ? (
                                                            <span className="flex items-center gap-2">
                                                                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                                                Loading...
                                                            </span>
                                                        ) : (
                                                            <>
                                                                <CreditCard className="h-4 w-4 mr-2" />
                                                                Unlock for {pricing.formatted}
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <p className="text-sm text-muted-foreground mt-4">
                                        {selectedLetter.description}
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="border-border h-[500px] flex items-center justify-center">
                                <div className="text-center">
                                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-muted-foreground">Select a letter to view</p>
                                </div>
                            </Card>
                        )}
                    </motion.div>
                </div>
            </main>
            <PricingModal open={pricingOpen} onOpenChange={setPricingOpen} />
        </div>
    );
};

export default LettersPage;
